/* Live engraving preview for the single plaque textarea (Plaque Line 1).
   - one textarea, max 3 lines, a line wraps by itself at 30 characters
   - the textarea value is always exactly what is shown on the plaque (and what is ordered)
   - preview is a visual guide only
   - if the product has a front-view urn image ([data-urn-live]) the same text is also drawn on the urn's plaque */
(function () {
  var MAX_LINES = 3;
  var MAX_CHARS = 30;
  var FONT = '"Playfair Display", Georgia, "Times New Roman", serif';

  /* Wrap/limit text. Returns { text, map } where map[i] is the new index of old index i. */
  function normalize(str) {
    str = String(str).replace(/\r\n?/g, '\n');
    var out = '';
    var map = new Array(str.length + 1);
    var line = 0; /* zero-based index of the current line */
    var len = 0; /* characters on the current line */
    var lastSpace = -1; /* index in out of the last space on the current line */

    for (var i = 0; i < str.length; i++) {
      var ch = str.charAt(i);
      map[i] = out.length;

      if (ch === '\n') {
        if (line >= MAX_LINES - 1) continue; /* no 4th line */
        out += '\n';
        line++;
        len = 0;
        lastSpace = -1;
        continue;
      }

      out += ch;
      len++;
      if (ch === ' ') lastSpace = out.length - 1;

      if (len > MAX_CHARS) {
        if (line >= MAX_LINES - 1) {
          /* last line is full: drop the extra character */
          out = out.slice(0, -1);
          len--;
        } else if (ch === ' ') {
          /* a space typed after a full line acts like Enter */
          out = out.slice(0, -1) + '\n';
          line++;
          len = 0;
          lastSpace = -1;
        } else if (lastSpace >= 0) {
          /* soft wrap: the last space on this line becomes the line break */
          out = out.slice(0, lastSpace) + '\n' + out.slice(lastSpace + 1);
          len = out.length - lastSpace - 1;
          line++;
          lastSpace = -1;
        } else {
          /* one very long word: break it at 30 */
          out = out.slice(0, -1) + '\n' + ch;
          map[i] = out.length - 1;
          line++;
          len = 1;
        }
      }
    }
    map[str.length] = out.length;
    return { text: out, map: map };
  }

  /* On phones the picture above the box is the animated scene (assets/scene-engrave.js), whose plaque sits elsewhere than on the
     old photo: use its corners (TL, TR, BR, BL as fractions of the square scene) so the close-up centres on the right spot. */
  function sceneQuad() {
    var s = window.__SCENE;
    if (!s || !s.quad || !window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) return null;
    var q = s.quad;
    return [q[0][0], q[0][1], q[1][0], q[1][1], q[2][0], q[2][1], q[3][0], q[3][1]].map(function (v) { return v / 1080; });
  }

  /* Tap the urn miniature: full-screen view of the WHOLE photo with the engraving on the plaque.
     In that view the customer can zoom in and out (+ / - buttons, click the picture, mouse wheel), drag to move around
     when zoomed in, and close it with Done, Esc, or a click on the dark area. */
  function initZoom(urn) {
    if (urn.getAttribute('data-zoom-ready') === '1') return;
    urn.setAttribute('data-zoom-ready', '1');
    var holder = null;
    var stageEl = urn.querySelector('.urn-live__stage');
    var btnEl = urn.querySelector('.urn-live__btn');
    var quad = sceneQuad() || parseQuad(urn.getAttribute('data-urn-quad'));
    var MAX_Z = 5;
    var zs = { z: 1, cx: 0.5, cy: 0.5 }; /* zoom factor and the centre of the visible window, as fractions of the photo */
    var dragged = false;

    function clamp(v, lo, hi) {
      return Math.max(lo, Math.min(hi, v));
    }

    /* size and position of the visible window of the photo (fractions) */
    function view() {
      var w = 1 / zs.z;
      return { w: w, x: clamp(zs.cx - w / 2, 0, 1 - w), y: clamp(zs.cy - w / 2, 0, 1 - w) };
    }

    function applyZoom(refit) {
      zs.z = clamp(zs.z, 1, MAX_Z);
      if (zs.z < 1.001) {
        zs.z = 1;
        zs.cx = zs.cy = 0.5;
      }
      var v = view();
      zs.cx = v.x + v.w / 2;
      zs.cy = v.y + v.w / 2;
      urn.style.setProperty('--zw', v.w);
      urn.style.setProperty('--zx', v.x);
      urn.style.setProperty('--zy', v.y);
      urn.classList.toggle('is-zoomed-in', zs.z > 1);
      if (refit !== false && urn.__refit) urn.__refit(); /* re-place and re-fit the engraving at the new size */
    }

    function plaqueCentre() {
      if (!quad) return { x: 0.5, y: 0.5 };
      return { x: (quad[0] + quad[2] + quad[4] + quad[6]) / 4, y: (quad[1] + quad[3] + quad[5] + quad[7]) / 4 };
    }

    /* photo coordinates (fractions) under a screen point */
    function pointAt(clientX, clientY) {
      var r = stageEl.getBoundingClientRect();
      var v = view();
      var u = r.width ? (clientX - r.left) / r.width : 0.5;
      var t = r.height ? (clientY - r.top) / r.height : 0.5;
      return { u: u, t: t, x: v.x + u * v.w, y: v.y + t * v.w };
    }

    function zoomBy(factor, clientX, clientY) {
      var nz = clamp(zs.z * factor, 1, MAX_Z);
      if (nz === zs.z) return;
      if (clientX == null) {
        /* from the + / - buttons: zoom toward the plaque the first time, then stay where we are */
        var c = zs.z === 1 ? plaqueCentre() : { x: zs.cx, y: zs.cy };
        zs.z = nz;
        zs.cx = c.x;
        zs.cy = c.y;
      } else {
        /* keep the point under the pointer fixed */
        var p = pointAt(clientX, clientY);
        var nw = 1 / nz;
        zs.z = nz;
        zs.cx = p.x - p.u * nw + nw / 2;
        zs.cy = p.y - p.t * nw + nw / 2;
      }
      applyZoom();
    }

    function setZoom(on) {
      if (on && !urn.classList.contains('is-zoomed')) {
        /* move the preview to <body> while enlarged so the site header cannot sit above it */
        holder = document.createComment('urn-live');
        urn.parentNode.insertBefore(holder, urn);
        document.body.appendChild(urn);
        zs.z = 1;
        zs.cx = zs.cy = 0.5;
        applyZoom(false);
      } else if (!on && holder) {
        holder.parentNode.replaceChild(urn, holder);
        holder = null;
      }
      urn.classList.toggle('is-zoomed', on);
      document.documentElement.style.overflow = on ? 'hidden' : '';
      if (urn.__refit) urn.__refit(); /* re-fit the text to the new size right away */
    }
    urn.__setZoom = setZoom;

    urn.addEventListener('click', function (e) {
      var t = e.target;
      if (urn.__pinched && Date.now() - urn.__pinched < 500) return; /* the end of a two-finger pinch is not a tap */
      if (t.closest && (t.closest('[data-urn-done]') || t.closest('[data-urn-zoom-in]') || t.closest('[data-urn-zoom-out]') || t.closest('[data-urn-closeup]'))) return;
      if (urn.closest && urn.closest('.is-typing')) return; /* no enlarge while the keyboard is open */
      if (!urn.classList.contains('is-zoomed')) {
        setZoom(true);
        return;
      }
      if (t.closest && t.closest('.urn-live__btn')) {
        /* a click on the picture itself: zoom in at that spot, or back out to the whole urn */
        if (dragged) return;
        if (zs.z === 1) {
          var p = pointAt(e.clientX, e.clientY);
          zs.z = 2.5;
          zs.cx = p.x;
          zs.cy = p.y;
          applyZoom();
        } else {
          zs.z = 1;
          applyZoom();
        }
        return;
      }
      setZoom(false); /* the dark area around the picture */
    });

    var zin = urn.querySelector('[data-urn-zoom-in]');
    var zout = urn.querySelector('[data-urn-zoom-out]');
    if (zin) zin.addEventListener('click', function () { zoomBy(1.6); });
    if (zout) zout.addEventListener('click', function () { zoomBy(1 / 1.6); });

    /* mouse wheel zooms around the pointer while the whole-urn view is open */
    if (stageEl) {
      stageEl.addEventListener(
        'wheel',
        function (e) {
          if (!urn.classList.contains('is-zoomed')) return;
          e.preventDefault();
          zoomBy(e.deltaY < 0 ? 1.2 : 1 / 1.2, e.clientX, e.clientY);
        },
        { passive: false }
      );
    }

    /* drag to move around the photo when zoomed in */
    if (btnEl) {
      var drag = null;
      btnEl.addEventListener('pointerdown', function (e) {
        if (!urn.classList.contains('is-zoomed') || zs.z <= 1 || (e.button != null && e.button !== 0)) return;
        drag = { x: e.clientX, y: e.clientY, cx: zs.cx, cy: zs.cy, moved: false };
        dragged = false;
        try {
          btnEl.setPointerCapture(e.pointerId);
        } catch (err) {}
      });
      btnEl.addEventListener('pointermove', function (e) {
        if (!drag) return;
        var dx = e.clientX - drag.x;
        var dy = e.clientY - drag.y;
        if (!drag.moved && Math.abs(dx) + Math.abs(dy) < 6) return;
        drag.moved = true;
        var r = stageEl.getBoundingClientRect();
        var w = 1 / zs.z;
        zs.cx = drag.cx - (dx / r.width) * w;
        zs.cy = drag.cy - (dy / r.height) * w;
        applyZoom();
      });
      var endDrag = function () {
        if (drag && drag.moved) {
          dragged = true; /* swallow the click that follows a drag */
          setTimeout(function () {
            dragged = false;
          }, 60);
        }
        drag = null;
      };
      btnEl.addEventListener('pointerup', endDrag);
      btnEl.addEventListener('pointercancel', endDrag);
    }

    document.addEventListener('keydown', function (e) {
      if (!urn.classList.contains('is-zoomed')) return;
      if (e.key === 'Escape') setZoom(false);
      else if (e.key === '+' || e.key === '=') zoomBy(1.6);
      else if (e.key === '-' || e.key === '_') zoomBy(1 / 1.6);
    });
  }

  /* ---- bending the flat plaque onto the angled photo ---- */
  var LOGICAL_W = 350; /* the flat plaque is drawn this wide (css px), then transformed onto the photo */

  /* quad = [x0,y0 ... x3,y3] (TL, TR, BR, BL) in px; returns a css matrix3d() that maps a w x h rectangle onto it */
  function quadMatrix(q, w, h) {
    var x0 = q[0], y0 = q[1], x1 = q[2], y1 = q[3], x2 = q[4], y2 = q[5], x3 = q[6], y3 = q[7];
    var dx1 = x1 - x2, dx2 = x3 - x2, dx3 = x0 - x1 + x2 - x3;
    var dy1 = y1 - y2, dy2 = y3 - y2, dy3 = y0 - y1 + y2 - y3;
    var a, b, d, e, g = 0, hh = 0;
    if (Math.abs(dx3) < 1e-9 && Math.abs(dy3) < 1e-9) {
      /* parallelogram: the two edge vectors are the columns */
      a = x1 - x0; d = y1 - y0;
      b = x3 - x0; e = y3 - y0;
    } else {
      var den = dx1 * dy2 - dx2 * dy1;
      g = (dx3 * dy2 - dx2 * dy3) / den;
      hh = (dx1 * dy3 - dx3 * dy1) / den;
      a = x1 - x0 + g * x1; b = x3 - x0 + hh * x3;
      d = y1 - y0 + g * y1; e = y3 - y0 + hh * y3;
    }
    var m = [a / w, d / w, 0, g / w, b / h, e / h, 0, hh / h, 0, 0, 1, 0, x0, y0, 0, 1];
    return 'matrix3d(' + m.join(',') + ')';
  }

  function parseQuad(str) {
    var n = String(str || '').split(',').map(parseFloat);
    if (n.length !== 8) return null;
    for (var i = 0; i < 8; i++) if (isNaN(n[i])) return null;
    return n;
  }

  /* put a flat-plaque element onto the photo; size = side of the square photo in px */
  function setWarp(el, quad, size, lw, lh) {
    if (!size) return;
    var q = quad.map(function (v) { return v * size; });
    el.style.width = lw + 'px';
    el.style.height = lh + 'px';
    el.style.transform = quadMatrix(q, lw, lh);
    el.classList.add('is-ready');
  }

  function init(root) {
    if (root.getAttribute('data-plaque-ready') === '1') return;
    var input = root.parentNode.querySelector('[data-plaque-input]');
    var box = root.querySelector('[data-plaque-box]');
    var textEl = root.querySelector('[data-plaque-text]');
    var inner = root.querySelector('[data-plaque-inner]');
    if (!input || !box || !textEl || !inner) return;
    root.setAttribute('data-plaque-ready', '1');

    /* optional angled photo of the whole urn with the same text bent onto its plaque (phone picture + gallery hero) */
    var fd = root.closest ? root.closest('[data-front-design]') : null;
    var urn = fd ? fd.querySelector('[data-urn-live]') : null;
    var urnText = urn ? urn.querySelector('[data-urn-text]') : null;
    var urnInner = urn ? urn.querySelector('[data-urn-inner]') : null;
    var urnScene = urn ? urn.querySelector('[data-urn-scene]') : null;
    var urnWarp = urn ? urn.querySelector('[data-urn-warp]') : null;
    var quad = urn ? sceneQuad() || parseQuad(urn.getAttribute('data-urn-quad')) : null;
    if (!urnText || !urnInner || !urnScene || !urnWarp || !quad) urn = null;

    var lw = LOGICAL_W;
    var lh = LOGICAL_W;
    var hero = { on: false, img: null, box: null, warp: null, text: null, inner: null, orig: null, token: 0 };
    if (urn) {
      var qw = Math.hypot(quad[2] - quad[0], quad[3] - quad[1]);
      var qh = Math.hypot(quad[6] - quad[0], quad[7] - quad[1]);
      /* data-urn-ratio: the plaque's true width/height when the photo is taken at a steep angle (the quad is foreshortened) */
      var trueRatio = parseFloat(urn.getAttribute('data-urn-ratio'));
      lh = Math.round(LOGICAL_W / (trueRatio > 0 ? trueRatio : qw / qh));

      /* thin burnt border 6% of the plaque height inside the edge (looked like 1/4 inch at 12.5%), following the scooped
         corners: each corner scoop is a circle centred on the plaque corner (radius ~19% of the height), so the inset
         line is the same circle grown by the inset. */
      var d = lh * 0.06;
      var notch = parseFloat(urn.getAttribute('data-urn-notch')) || 0.19;
      var rr = lh * notch + d;
      var a = Math.sqrt(rr * rr - d * d);
      var f = function (n) {
        return Math.round(n * 100) / 100;
      };
      var arc = 'A' + f(rr) + ' ' + f(rr) + ' 0 0 0 ';
      var path =
        'M' + f(a) + ' ' + f(d) +
        'L' + f(lw - a) + ' ' + f(d) + arc + f(lw - d) + ' ' + f(a) +
        'L' + f(lw - d) + ' ' + f(lh - a) + arc + f(lw - a) + ' ' + f(lh - d) +
        'L' + f(a) + ' ' + f(lh - d) + arc + f(d) + ' ' + f(lh - a) +
        'L' + f(d) + ' ' + f(a) + arc + f(a) + ' ' + f(d) + 'Z';
      var svgNS = 'http://www.w3.org/2000/svg';
      var svg = document.createElementNS(svgNS, 'svg');
      svg.setAttribute('viewBox', '0 0 ' + lw + ' ' + lh);
      svg.setAttribute('aria-hidden', 'true');
      svg.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;overflow:visible;pointer-events:none';
      var pathEl = document.createElementNS(svgNS, 'path');
      pathEl.setAttribute('d', path);
      pathEl.setAttribute('fill', 'none');
      pathEl.setAttribute('stroke', 'rgb(58,33,19)');
      pathEl.setAttribute('stroke-opacity', '0.6');
      pathEl.setAttribute('stroke-width', '2');
      pathEl.setAttribute('stroke-linejoin', 'round');
      svg.appendChild(pathEl);
      /* data-urn-border="0": the photo already has the real engraved border, so draw none of our own */
      if (urn.getAttribute('data-urn-border') !== '0') urnWarp.insertBefore(svg, urnWarp.firstChild);
    }

    var placeholder = (input.getAttribute('placeholder') || '')
      .replace(/\r\n?/g, '\n')
      .replace(/^\(e\.g\.\)\s*/i, '');
    var composing = false;
    var demo = root.querySelector('[data-plaque-demo]');
    var demoText = demo ? demo.querySelector('[data-plaque-demo-text]') : null;

    /* "Done" shows the urn in the plaque's place with the saved card; "Retype" brings the plaque back.
       The class goes on the plaque block and on the whole front-design block (the urn picture lives outside the plaque block). */
    function setConfirmed(on) {
      root.classList.toggle('is-confirmed', on);
      if (fd) fd.classList.toggle('is-confirmed', on);
      /* back to editing: the picture goes back to normal at once (but while the keyboard view is open it stays close) */
      if (!on && urn && !typing) {
        urn.classList.remove('is-closeup', 'is-zooming');
        userZoomed = false;
      }
    }

    /* mouse (computer) or touch (phone, tablet): decides "Click" or "Tap" in the typing demo */
    var mouseUser = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
    var demoPhrase = (mouseUser ? 'Click' : 'Tap') + ' to enter a name';

    /* ---- typing straight onto the plaque ---- */
    var phone = window.matchMedia ? window.matchMedia('(max-width: 749px)') : { matches: false };
    var stage = urn ? urn.querySelector('.urn-live__stage') : null;
    var doneBtn = urn ? urn.querySelector('[data-urn-done]') : null;
    /* the chocolate Done / Save button under the plaque, and the "saved and ready" card it reveals */
    var saveBtn = root.querySelector('[data-plaque-save]');
    var savedBox = root.querySelector('[data-plaque-saved]');
    function endEl() {
      return saveBtn && saveBtn.offsetHeight ? saveBtn : box;
    }
    var typing = false;
    var typingRuns = 0;
    var typingTimers = [];

    function vvHeight() {
      var v = window.visualViewport;
      return v ? v.height : window.innerHeight;
    }

    function vvTop() {
      var v = window.visualViewport;
      return v ? v.offsetTop : 0;
    }

    /* sticky/fixed site header elements */
    function stickyHeaders() {
      var out = [];
      var els = document.querySelectorAll('header-component, #header-group, header');
      for (var i = 0; i < els.length; i++) {
        var cs = window.getComputedStyle(els[i]);
        if (cs.position === 'fixed' || cs.position === 'sticky') out.push(els[i]);
      }
      return out;
    }

    /* height of a sticky/fixed site header that is currently on screen */
    function headerInset() {
      var n = 0;
      var els = stickyHeaders();
      for (var i = 0; i < els.length; i++) {
        var r = els[i].getBoundingClientRect();
        if (r.height > 0 && r.bottom > 0 && r.top < 80) n = Math.max(n, r.height);
      }
      return n;
    }

    /* the tall sticky header would take ~40% of the little space left above the keyboard: slide it away while typing */
    /* (a class on <html> + a stylesheet rule, so the theme's own scroll script cannot undo it) */
    function hideHeaders(on) {
      document.documentElement.classList.toggle('sw-typing', on);
    }

    /* On a phone the urn picture is a window onto the square photo.
       - resting: wide enough to show the outline of the whole urn (--cw = window width as a fraction of the photo,
         --cx/--cy = its top-left corner, --ar = window height / width);
       - while typing: a closer window (--cwt) centred on the plaque, as tall as fits above the keyboard (wantH, px;
         capped at 62% of the width, floored at 90px) so the engraving stays readable.
       Returns the height actually used. */
    function fitStage(wantH) {
      if (!urn || !stage || urn.classList.contains('is-zoomed')) return 0;
      var W = stage.clientWidth;
      if (!W) return 0;
      var cs = window.getComputedStyle(urn);
      var num = function (name, dflt) {
        var v = parseFloat(cs.getPropertyValue(name));
        return isNaN(v) ? dflt : v;
      };
      var cw = num('--cw', 0.66); /* typing keeps the same photo size: the close-up is a smooth transform on top (see typingZoomIn) */
      var S = W / cw; /* the whole square photo, in px, at this zoom */
      var H, tx, ty;
      if (typing) {
        var cap = Math.round(W * 0.62);
        H = Math.max(90, Math.min(wantH > 0 ? Math.floor(wantH) : cap, cap));
        tx = Math.max(0, Math.min(S - W, num('--cx', 0) * S));
        ty = Math.max(0, Math.min(S - H, num('--cy', 0) * S));
      } else {
        H = Math.round(W * num('--ar', 0.62));
        tx = Math.max(0, Math.min(S - W, num('--cx', 0) * S));
        ty = Math.max(0, Math.min(S - H, num('--cy', 0) * S));
      }
      urn.style.setProperty('--scene-w', S + 'px');
      urn.style.setProperty('--urn-h', H + 'px');
      urn.style.setProperty('--tx', -tx + 'px');
      urn.style.setProperty('--ty', -ty + 'px');
      return H;
    }

    /* While the keyboard is open: make urn + plaque fit the visible area above it, and scroll them into view. */
    function layoutTyping() {
      if (!typing || !urn || !stage) return;
      var inset = headerInset() + 8;
      var room = vvHeight() - inset - 8;
      var pr = box.getBoundingClientRect();
      var below = endEl().getBoundingClientRect().bottom - pr.bottom;
      var h = fitStage(room - pr.height - below - 12);
      var ur = urn.getBoundingClientRect();
      pr = box.getBoundingClientRect();
      var total = endEl().getBoundingClientRect().bottom - ur.top;
      if (total > room + 1) {
        fitStage(h - (total - room));
        ur = urn.getBoundingClientRect();
      }
      var delta = ur.top - (vvTop() + inset);
      if (Math.abs(delta) > 3) window.scrollBy(0, delta);
      placeUrn();
      if (urn.classList.contains('is-closeup') && !userZoomed) computeCloseup(); /* the window height may have changed */
    }

    /* ---- close-up after "Done" ----
       The scene (the square photo) is scaled up and slid so the plaque sits in the middle of the picture window; a CSS
       transition makes it a slow glide. The warp (the engraving) lives inside the scene, so it follows. */
    var closeupTimer = 0;
    var closeupDelay = 0;
    var userZoomed = false; /* the customer pinched: stop re-centring the close-up for them */

    function computeCloseup() {
      if (!urn || !stage || !urnScene) return false;
      var W = stage.clientWidth;
      var H = stage.clientHeight;
      var S = urnScene.offsetWidth; /* layout size of the photo, unaffected by transforms */
      if (!W || !H || !S) return false;
      var bx = urnScene.offsetLeft;
      var by = urnScene.offsetTop;
      var xc = (quad[0] + quad[2] + quad[4] + quad[6]) / 4;
      var yc = (quad[1] + quad[3] + quad[5] + quad[7]) / 4;
      var pw = Math.hypot(quad[2] - quad[0], quad[3] - quad[1]); /* plaque width as a fraction of the photo */
      var z = pw > 0 ? (0.7 * W) / (pw * S) : 2.5; /* the plaque fills about 70% of the picture width */
      z = Math.max(1.4, Math.min(3.6, z));
      var a = W / 2 - bx - z * xc * S;
      var b = H / 2 - by - z * yc * S;
      /* never slide past the edge of the photo (no empty strips) */
      a = Math.max(W - z * S - bx, Math.min(-bx, a));
      b = Math.max(H - z * S - by, Math.min(-by, b));
      urn.style.setProperty('--cz-s', z);
      urn.style.setProperty('--cz-x', a + 'px');
      urn.style.setProperty('--cz-y', b + 'px');
      return true;
    }

    function zoomMotion() {
      urn.classList.add('is-zooming');
      clearTimeout(closeupTimer);
      closeupTimer = setTimeout(function () {
        urn.classList.remove('is-zooming');
      }, 2100);
    }

    function closeupOn() {
      if (!urn || urn.hidden || urn.classList.contains('is-zoomed') || !computeCloseup()) return;
      userZoomed = false;
      zoomMotion();
      urn.classList.add('is-closeup');
    }

    function closeupOff() {
      if (!urn) return;
      userZoomed = false;
      zoomMotion();
      urn.classList.remove('is-closeup');
    }

    /* The moment the customer taps the box: the plaque on the urn picture glides toward them (same slow ease as before),
       so they watch their engraving appear at a readable size while they type. */
    function typingZoomIn() {
      if (!urn || urn.hidden || urn.classList.contains('is-zoomed')) return;
      clearTimeout(closeupDelay);
      userZoomed = false;
      if (!computeCloseup()) return;
      zoomMotion();
      urn.classList.add('is-closeup');
    }

    /* Typing ended (Done, or the customer tapped elsewhere): the picture was resized, so keep the same framing for a
       beat, then ease back out to the whole urn. */
    function typingEnded() {
      clearTimeout(closeupDelay);
      if (!urn || urn.hidden || urn.classList.contains('is-zoomed') || !urn.classList.contains('is-closeup')) return;
      urn.classList.remove('is-zooming');
      if (!userZoomed) computeCloseup();
      closeupDelay = setTimeout(closeupOff, 350);
    }

    /* After Done the urn takes the plaque's place: make sure it starts close on the engraving (computers never typed in a
       close view), then ease out so the whole urn is nicely framed. */
    function frameUrn() {
      if (!urn || urn.hidden || urn.classList.contains('is-zoomed')) return;
      if (!urn.classList.contains('is-closeup')) {
        if (!computeCloseup()) return;
        urn.classList.remove('is-zooming');
        urn.classList.add('is-closeup');
        void urn.offsetWidth; /* settle this starting frame so the next change animates */
      } else if (!userZoomed) {
        urn.classList.remove('is-zooming');
        computeCloseup();
        void urn.offsetWidth;
      }
      closeupOff();
    }

    /* After "Done": the keyboard slides down in steps (the visible area grows) and the page layout changes under us
       (typing view off, header back, plaque swapped for the urn), so a single scroll lands in the wrong place.
       Instead, for about 3 s, every 40 ms we measure where the urn is in the VISIBLE area and nudge the page so its
       middle sits in the middle of that area. Stops at once if the visitor touches, scrolls or types. */
    var centreRaf = 0;
    var centreStop = null;
    function keepUrnCentred() {
      clearTimeout(centreRaf);
      if (centreStop) centreStop();
      var fig = urn && !urn.hidden ? urn : null;
      var target = fig || savedBox;
      if (!target) return;
      var inset = headerInset(); /* measured once, so a header that hides while we scroll cannot make us bounce */
      var t0 = Date.now();
      var quiet = 0; /* consecutive frames with nothing left to correct */
      var onUser = function () {
        stop();
      };
      var stop = function () {
        clearTimeout(centreRaf);
        ['touchstart', 'wheel', 'keydown', 'mousedown'].forEach(function (ev) {
          window.removeEventListener(ev, onUser, true);
        });
        centreStop = null;
      };
      centreStop = stop;
      ['touchstart', 'wheel', 'keydown', 'mousedown'].forEach(function (ev) {
        window.addEventListener(ev, onUser, { capture: true, passive: true });
      });
      var tick = function () {
        if (Date.now() - t0 > 3000) {
          stop();
          return;
        }
        if (Date.now() - t0 > 120 && target.getBoundingClientRect().height > 0) {
          var r = target.getBoundingClientRect();
          var top = vvTop() + inset;
          var bottom = vvTop() + vvHeight();
          var delta = r.top + r.height / 2 - (top + bottom) / 2;
          if (r.height > bottom - top) delta = r.top - top; /* taller than the visible area: align its top instead */
          if (Math.abs(delta) > 2) {
            window.scrollBy({ top: delta, left: 0, behavior: 'instant' }); /* never the page's smooth scrolling: it would lag behind the frames */
            quiet = 0;
          } else {
            quiet++;
          }
          if (quiet > 18 && Date.now() - t0 > 1200) {
            stop(); /* settled for ~0.75 s */
            return;
          }
        }
        centreRaf = setTimeout(tick, 40);
      };
      centreRaf = setTimeout(tick, 40);
    }

    function setTyping(on) {
      if (typing === on) return;
      typing = on;
      if (fd) fd.classList.toggle('is-typing', on);
      hideHeaders(on);
      typingTimers.forEach(clearTimeout);
      typingTimers = [];
      if (on) {
        typingRuns = 0;
        layoutTyping();
        typingZoomIn();
        /* the keyboard takes a moment to slide up and the browser resizes the visible area in steps: re-check */
        [80, 250, 500, 900].forEach(function (ms) {
          typingTimers.push(setTimeout(layoutTyping, ms));
        });
      } else {
        fitStage();
        placeUrn();
        typingEnded();
      }
    }

    function onViewport() {
      if (typing && typingRuns++ < 20) layoutTyping();
    }

    function fitInto(el, box, floor) {
      var w = box.clientWidth;
      var h = box.clientHeight;
      if (!w || !h) return;
      var size = Math.min(h * 0.62, w * 0.085);
      var min = Math.max(floor, w * 0.02);
      el.style.fontSize = size + 'px';
      var guard = 80;
      while (guard-- > 0 && size > min && (el.offsetWidth > w || el.offsetHeight > h)) {
        size *= 0.96;
        el.style.fontSize = size + 'px';
      }
    }

    /* ---- the urn photo: phone picture (always) and the big gallery photo (while typing) ---- */
    /* the photo on gallery slide 1; remembered at load, because the theme can reorder slides while it scrolls */
    var heroTarget = null;
    var galleryImg = function () {
      if (heroTarget && heroTarget.isConnected) return heroTarget;
      heroTarget =
        document.querySelector('media-gallery slideshow-slide[style*="--slide-0;"] .product-media img') ||
        document.querySelector('media-gallery slideshow-slide .product-media img');
      return heroTarget;
    };
    galleryImg();

    function placeUrn() {
      if (urn) setWarp(urnWarp, quad, urnScene.offsetWidth, lw, lh);
    }

    function placeHero() {
      if (!hero.on || !hero.img || !hero.box) return;
      var img = hero.img;
      var p = hero.box.parentNode;
      if (!p) return;
      /* layout metrics (offset*), not on-screen rectangles: the photo may be zoomed by a transform while the customer types */
      var w = img.offsetWidth;
      var h = img.offsetHeight;
      if (!w || !h) return;
      var cover = window.getComputedStyle(img).objectFit === 'cover';
      var s = cover ? Math.max(w, h) : Math.min(w, h);
      var st = hero.box.style;
      st.left = img.offsetLeft + (w - s) / 2 + 'px';
      st.top = img.offsetTop + (h - s) / 2 + 'px';
      st.width = s + 'px';
      st.height = s + 'px';
      setWarp(hero.warp, quad, s, lw, lh);
    }

    function heroOff() {
      hero.token++;
      if (hero.on && hero.img && hero.orig) {
        var o = hero.orig;
        hero.img.src = o.src;
        if (o.srcset != null) hero.img.setAttribute('srcset', o.srcset);
        if (o.sizes != null) hero.img.setAttribute('sizes', o.sizes);
        hero.img.removeAttribute('data-urn-hero');
      }
      if (hero.box && hero.box.parentNode) hero.box.parentNode.removeChild(hero.box);
      hero.on = false;
      hero.box = hero.img = hero.warp = hero.text = hero.inner = hero.orig = null;
      if (hero.ro) {
        hero.ro.disconnect();
        hero.ro = null;
      }
    }

    function heroOn(textValue) {
      if (!urn) return;
      if (hero.on) {
        hero.text.textContent = textValue;
        fitInto(hero.text, hero.inner, 6);
        placeHero();
        return;
      }
      var img = galleryImg();
      if (!img || !img.parentNode) return;
      var token = ++hero.token;
      var src = urn.getAttribute('data-urn-img');
      var pre = new Image();
      pre.onload = function () {
        if (token !== hero.token || hero.on) return;
        var p = img.parentNode;
        if (window.getComputedStyle(p).position === 'static') p.style.position = 'relative';
        hero.orig = { src: img.getAttribute('src'), srcset: img.getAttribute('srcset'), sizes: img.getAttribute('sizes') };
        hero.img = img;
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = src;
        img.setAttribute('data-urn-hero', '1');

        var b = document.createElement('span');
        b.className = 'urn-hero';
        b.setAttribute('aria-hidden', 'true');
        var w = urnWarp.cloneNode(true);
        w.className = 'urn-hero__warp';
        b.appendChild(w);
        p.appendChild(b);
        hero.box = b;
        hero.warp = w;
        hero.text = w.querySelector('[data-urn-text]');
        hero.inner = w.querySelector('[data-urn-inner]');
        hero.text.style.fontFamily = FONT;
        hero.on = true;
        hero.text.textContent = textValue;
        fitInto(hero.text, hero.inner, 6);
        placeHero();
        if (typeof ResizeObserver === 'function') {
          hero.ro = new ResizeObserver(placeHero);
          hero.ro.observe(img);
        }
      };
      pre.src = src;
    }

    /* ---- computers: the big gallery photo glides in on the plaque when the customer taps the box, and eases back out
       when they press "Preview on the urn" (same slow ease as the phone picture). The photo and the engraving laid on it
       live in the same box, so one transform moves both. ---- */
    var heroZoomed = null;
    var heroZoomTimer = 0;
    var HERO_EASE = 'transform 1.9s cubic-bezier(0.45, 0, 0.2, 1)';

    function heroZoomTarget() {
      var img = galleryImg();
      if (!img || !img.parentNode || !urn || !quad) return null;
      var p = img.parentNode;
      var w = img.offsetWidth;
      var h = img.offsetHeight;
      var W = p.offsetWidth;
      var H = p.offsetHeight;
      if (!w || !h || !W || !H) return null;
      var cover = window.getComputedStyle(img).objectFit === 'cover';
      var side = cover ? Math.max(w, h) : Math.min(w, h); /* the square photo, in px */
      var left = img.offsetLeft + (w - side) / 2;
      var top = img.offsetTop + (h - side) / 2;
      var xc = (quad[0] + quad[2] + quad[4] + quad[6]) / 4;
      var yc = (quad[1] + quad[3] + quad[5] + quad[7]) / 4;
      var pw = Math.hypot(quad[2] - quad[0], quad[3] - quad[1]); /* plaque width as a fraction of the photo */
      var cx = left + xc * side; /* the plaque centre, in the box */
      var cy = top + yc * side;
      var z = pw > 0 ? (0.5 * Math.min(W, H)) / (pw * side) : 2.5; /* the plaque fills about half of the photo area */
      z = Math.max(1.4, Math.min(3.6, z));
      /* scale about the plaque, then slide it to the middle of the box, never past the edge of the photo */
      var tx = W / 2 - cx;
      var ty = H / 2 - cy;
      var loX = W - (cx + (left + side - cx) * z);
      var hiX = -(cx + (left - cx) * z);
      var loY = H - (cy + (top + side - cy) * z);
      var hiY = -(cy + (top - cy) * z);
      tx = loX > hiX ? (loX + hiX) / 2 : Math.max(loX, Math.min(hiX, tx));
      ty = loY > hiY ? (loY + hiY) / 2 : Math.max(loY, Math.min(hiY, ty));
      return { p: p, cx: cx, cy: cy, z: z, tx: tx, ty: ty };
    }

    function heroZoomIn() {
      if (phone.matches || !urn) return;
      clearTimeout(heroZoomTimer);
      var t = heroZoomTarget();
      if (!t) return;
      var st = t.p.style;
      st.transformOrigin = t.cx + 'px ' + t.cy + 'px';
      st.willChange = 'transform';
      st.transition = calm ? 'none' : HERO_EASE;
      st.transform = 'translate(' + t.tx + 'px, ' + t.ty + 'px) scale(' + t.z + ')';
      heroZoomed = t.p;
    }

    function heroZoomOut(delay) {
      clearTimeout(heroZoomTimer);
      if (!heroZoomed) return;
      heroZoomTimer = setTimeout(function () {
        var p = heroZoomed;
        heroZoomed = null;
        if (!p) return;
        p.style.transition = calm ? 'none' : HERO_EASE;
        p.style.transform = 'none';
      }, delay || 0);
    }

    /* bring the gallery to slide 1 (the photo that carries the render) */
    function jumpToHero() {
      if (phone.matches) return; /* on a phone the urn picture sits right above the plaque, no need to jump away */
      var t = document.querySelector('media-gallery .slideshow-controls__thumbnail');
      if (t && t.getAttribute('aria-selected') !== 'true') t.click();
    }

    function plaqueChosen() {
      return !!urn && !urn.hidden;
    }

    /* the textarea shows the engraving itself: same font size as the fitted preview text (never under 16px),
       and padded down so the lines sit in the middle of the plaque like the engraving will */
    function styleInput() {
      var size = Math.max(16, parseFloat(textEl.style.fontSize) || 16);
      var lines = String(textEl.textContent || '').split('\n').length;
      var pad = box.clientHeight * 0.15 + Math.max(0, (inner.clientHeight - lines * size * 1.45) / 2);
      input.style.fontSize = size + 'px';
      input.style.paddingTop = pad + 'px';
      if (demo) {
        /* the typing demo has its own (longer) wording: shrink it until its widest line fits the plaque, then centre its 3 lines */
        var dsize = size;
        var meas = root.querySelector('[data-plaque-demo-measure]'); /* invisible copy of the full sentence, always laid out */
        if (meas) {
          if (meas.textContent !== demoPhrase) meas.textContent = demoPhrase;
          meas.style.fontSize = size + 'px';
          var avail = inner.clientWidth;
          if (meas.offsetWidth > avail && avail > 0) dsize = Math.max(11, (size * avail) / meas.offsetWidth);
        }
        demo.style.fontSize = dsize + 'px';
        demo.style.paddingTop = box.clientHeight * 0.15 + Math.max(0, (inner.clientHeight - 1 * dsize * 1.45) / 2) + 'px';
      }
    }

    function fit() {
      fitInto(textEl, inner, 6);
      styleInput();
      if (urn) {
        fitInto(urnText, urnInner, 6);
        if (!typing) fitStage();
        placeUrn();
        placeHero();
        /* keep an open close-up centred if the picture is resized (no animation) */
        if (urn.classList.contains('is-closeup') && !urn.classList.contains('is-zooming') && !userZoomed) computeCloseup();
      }
    }

    function render() {
      var v = input.value;
      var empty = v.replace(/\s/g, '') === '';
      root.classList.toggle('is-placeholder', empty);
      root.classList.toggle('is-filled', !empty);
      textEl.textContent = empty ? placeholder : v;
      if (urn) {
        urn.classList.toggle('is-placeholder', empty);
        urnText.textContent = empty ? placeholder : v;
        if (!empty && plaqueChosen()) heroOn(v);
        else heroOff();
      }
      fit();
    }

    function apply() {
      if (composing) return;
      setConfirmed(false);
      var before = input.value;
      var res = normalize(before);
      if (res.text !== before) {
        var caret = input.selectionStart;
        var focused = document.activeElement === input;
        input.value = res.text;
        if (focused && caret != null) {
          var pos = res.map[Math.min(caret, res.map.length - 1)];
          input.setSelectionRange(pos, pos);
        }
      }
      render();
    }

    input.addEventListener('input', apply);
    input.addEventListener('compositionstart', function () {
      composing = true;
    });
    input.addEventListener('compositionend', function () {
      composing = false;
      apply();
    });
    input.addEventListener('change', apply);

    input.addEventListener('focus', function () {
      root.classList.add('is-focus');
      demoSync();
      if (urn && !urn.hidden && phone.matches) setTyping(true);
      else if (urn && !phone.matches) heroZoomIn();
    });
    input.addEventListener('blur', function () {
      root.classList.remove('is-focus');
      heroZoomOut(250);
      setTimeout(demoSync, 400);
      setTimeout(function () {
        if (document.activeElement !== input) setTyping(false);
      }, 120);
    });
    input.addEventListener('input', demoSync);
    if (saveBtn) {
      ['pointerdown', 'mousedown'].forEach(function (ev) {
        saveBtn.addEventListener(ev, function (e) {
          e.preventDefault(); /* keep the keyboard steady until the click */
        });
      });
      saveBtn.addEventListener('click', function () {
        var empty = input.value.replace(/\s/g, '') === '';
        input.blur(); /* closes the keyboard and the phone typing view */
        heroZoomOut(250); /* computers: the big photo eases back out too */
        root.classList.remove('is-focus');
        setTyping(false);
        if (empty) return;
        setConfirmed(true);
        /* the plaque was close while typing: once the picture has taken its place, ease out so the whole urn is framed */
        clearTimeout(closeupDelay);
        closeupDelay = setTimeout(function () {
          if (root.classList.contains('is-confirmed')) frameUrn();
        }, 350);
        /* the keyboard is sliding away and the page re-lays itself out: keep the urn centred in what is visible */
        keepUrnCentred();
      });
    }
    /* the real "Enter a name" button opens the same editor as tapping the plaque */
    var startBtn = root.querySelector('[data-plaque-start]');
    if (startBtn) {
      startBtn.addEventListener('click', function () {
        input.focus();
      });
    }

    /* the big corner button: zoom out to the whole urn, and back in on the engraving */
    var closeupBtn = urn ? urn.querySelector('[data-urn-closeup]') : null;
    if (closeupBtn) {
      closeupBtn.addEventListener('click', function () {
        clearTimeout(closeupDelay);
        if (urn.classList.contains('is-closeup')) closeupOff();
        else closeupOn();
      });
    }

    /* ---- two-finger pinch on the urn picture: zoom in / out and move around, any time (also while typing) ---- */
    function initPinch() {
      var btn = urn ? urn.querySelector('.urn-live__btn') : null;
      if (!btn || !stage || !urnScene) return;
      var pts = {};
      var g = null;
      var MAXS = 6;
      var prop = function (n) {
        return parseFloat(urn.style.getPropertyValue(n)) || 0;
      };
      var two = function () {
        var k = Object.keys(pts);
        return k.length >= 2 ? [pts[k[0]], pts[k[1]]] : null;
      };
      /* current scale + translation of the scene (px): the close-up values, or the resting window */
      var cur = function () {
        if (urn.classList.contains('is-closeup')) return { s: prop('--cz-s') || 1, x: prop('--cz-x'), y: prop('--cz-y') };
        return { s: 1, x: prop('--tx'), y: prop('--ty') };
      };
      btn.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'touch' || urn.classList.contains('is-zoomed')) return;
        pts[e.pointerId] = { x: e.clientX, y: e.clientY };
        var p = two();
        if (!p) return;
        var c = cur();
        g = {
          d: Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) || 1,
          mx: (p[0].x + p[1].x) / 2,
          my: (p[0].y + p[1].y) / 2,
          s: c.s,
          x: c.x,
          y: c.y
        };
        /* from now on the picture follows the fingers: no glide */
        clearTimeout(closeupDelay);
        clearTimeout(closeupTimer);
        urn.classList.remove('is-zooming');
        if (!urn.classList.contains('is-closeup')) {
          urn.style.setProperty('--cz-s', c.s);
          urn.style.setProperty('--cz-x', c.x + 'px');
          urn.style.setProperty('--cz-y', c.y + 'px');
          urn.classList.add('is-closeup');
        }
        userZoomed = true;
      });
      btn.addEventListener('pointermove', function (e) {
        if (!pts[e.pointerId]) return;
        pts[e.pointerId] = { x: e.clientX, y: e.clientY };
        var p = two();
        if (!p || !g) return;
        var d = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) || 1;
        var mx = (p[0].x + p[1].x) / 2;
        var my = (p[0].y + p[1].y) / 2;
        var W = stage.clientWidth;
        var H = stage.clientHeight;
        var S = urnScene.offsetWidth;
        var bx = urnScene.offsetLeft;
        var by = urnScene.offsetTop;
        var r = stage.getBoundingClientRect();
        var ox = r.left + stage.clientLeft + bx;
        var oy = r.top + stage.clientTop + by;
        var s = Math.max(1, Math.min(MAXS, (g.s * d) / g.d));
        /* the photo point that was between the fingers at the start stays between them */
        var u = (g.mx - ox - g.x) / g.s;
        var v = (g.my - oy - g.y) / g.s;
        var x = mx - ox - u * s;
        var y = my - oy - v * s;
        /* never slide past the edge of the photo */
        x = Math.max(W - s * S - bx, Math.min(-bx, x));
        y = Math.max(H - s * S - by, Math.min(-by, y));
        urn.style.setProperty('--cz-s', s);
        urn.style.setProperty('--cz-x', x + 'px');
        urn.style.setProperty('--cz-y', y + 'px');
        urn.__pinched = Date.now();
      });
      var up = function (e) {
        if (!pts[e.pointerId]) return;
        delete pts[e.pointerId];
        if (g) urn.__pinched = Date.now();
        if (!two()) g = null;
      };
      btn.addEventListener('pointerup', up);
      btn.addEventListener('pointercancel', up);
      /* two fingers on the picture are for the picture, not for scrolling or zooming the whole page */
      btn.addEventListener(
        'touchmove',
        function (e) {
          if (e.touches && e.touches.length >= 2) e.preventDefault();
        },
        { passive: false }
      );
    }
    if (urn) initPinch();

    /* "Retype my engraving": back to the plaque with the caret in it */
    var retypeBtn = root.querySelector('[data-plaque-retype]');
    if (retypeBtn) {
      retypeBtn.addEventListener('click', function () {
        setConfirmed(false);
        input.scrollIntoView({ block: 'center', behavior: 'smooth' });
        input.focus();
      });
    }
    if (doneBtn) {
      /* keep the field focused while the button is pressed, then close the keyboard on click */
      ['pointerdown', 'mousedown'].forEach(function (ev) {
        doneBtn.addEventListener(ev, function (e) {
          e.preventDefault();
        });
      });
      doneBtn.addEventListener('click', function () {
        if (urn.classList.contains('is-zoomed') && urn.__setZoom) urn.__setZoom(false); /* closes the whole-urn view */
        else input.blur();
      });
    }

    /* Clicking the big gallery photo while it shows the customer's engraving: the theme's own zoom viewer only knows the
       original photo (with the sample engraving), so show our whole-urn view instead (same one the phone picture opens). */
    if (urn) {
      var closeThemeZoom = function () {
        var d = document.querySelector('zoom-dialog dialog[open]');
        if (!d) return;
        var b = d.querySelector('.dialog-zoomed-gallery__close-button');
        if (b) b.click();
        else if (d.close) d.close();
      };
      /* on the window, in the capture phase: runs before the theme's own click handling */
      window.addEventListener(
        'click',
        function (e) {
          if (!hero.on || !hero.img || !urn.__setZoom || urn.classList.contains('is-zoomed')) return;
          var pm = hero.img.closest ? hero.img.closest('.product-media') : null;
          if (!pm || !pm.contains(e.target)) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          urn.__setZoom(true);
          /* safety net: if the theme's viewer still opened, close it so ours stays on top */
          [0, 60, 200, 500].forEach(function (ms) {
            setTimeout(closeThemeZoom, ms);
          });
        },
        true
      );
    }
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onViewport);
      window.visualViewport.addEventListener('scroll', onViewport);
    }

    /* ---- alive placeholder ----
       While the plaque is on screen and nobody has tapped it, the example engraving types itself in the grey placeholder
       with a caret that moves forward, the border breathes (CSS .is-demo), then it fades and starts again.
       It stops the moment the customer taps, types, picks another front design, or leaves the tab.
       Visual only: the textarea (the real order field) is never touched. */
    var DEMO_FIRST_MS = 450; /* caret blinks alone before the first letter */
    var DEMO_HOLD_MS = 3500; /* finished sentence stays, caret blinking "your turn", before it fades and replays */
    var DEMO_REST_MS = 1000; /* fade out + empty plaque before the next run */
    var calm = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var demoOn = false;
    var demoTimer = 0;
    var demoVisible = false;

    function demoAllowed() {
      return (
        !!demo &&
        !!demoText &&
        !calm &&
        demoVisible &&
        !document.hidden &&
        root.offsetParent !== null &&
        document.activeElement !== input &&
        input.value.replace(/\s/g, '') === '' &&
        !root.classList.contains('is-confirmed')
      );
    }

    function demoStop() {
      clearTimeout(demoTimer);
      demoTimer = 0;
      if (!demoOn) return;
      demoOn = false;
      root.classList.remove('is-demo');
      demo.classList.remove('is-fading');
      demoText.textContent = '';
    }

    function demoRun(n) {
      demoTimer = 0;
      if (!demoAllowed()) {
        demoStop();
        return;
      }
      if (!demoOn) {
        demoOn = true;
        root.classList.add('is-demo');
      }
      demo.classList.remove('is-fading');
      if (n <= demoPhrase.length) {
        demoText.textContent = demoPhrase.slice(0, n);
        var ch = demoPhrase.charAt(n - 1);
        var wait = n === 0 ? DEMO_FIRST_MS : ch === '\n' ? 450 : 65 + Math.random() * 55;
        demoTimer = setTimeout(function () {
          demoRun(n + 1);
        }, wait);
      } else {
        /* the finished prompt stays a few seconds with the caret blinking, then fades and types itself again */
        demoTimer = setTimeout(function () {
          demo.classList.add('is-fading');
          demoTimer = setTimeout(function () {
            demoText.textContent = '';
            demoRun(0);
          }, DEMO_REST_MS);
        }, DEMO_HOLD_MS);
      }
    }

    function demoSync() {
      if (demoAllowed()) {
        if (!demoOn || !demoTimer) {
          clearTimeout(demoTimer);
          demoRun(0);
        }
      } else {
        demoStop();
      }
    }

    /* "near": the plaque is on screen OR still up to ~1.5 screens below the fold (people scroll fast on a phone, so it must
       already be alive when it arrives), and stays alive until it has scrolled a little above the top */
    var DEMO_AHEAD_PX = 1400;
    var DEMO_BEHIND_PX = 300;
    var demoRaf = 0;

    function demoNear() {
      var r = box.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      return r.top < vh + DEMO_AHEAD_PX && r.bottom > -DEMO_BEHIND_PX;
    }

    function demoCheck() {
      demoRaf = 0;
      var v = demoNear();
      if (v !== demoVisible) {
        demoVisible = v;
        demoSync();
      }
    }

    function demoQueue() {
      if (!demoRaf) demoRaf = window.requestAnimationFrame ? window.requestAnimationFrame(demoCheck) : setTimeout(demoCheck, 50);
    }

    if (demo && demoText) {
      window.addEventListener('scroll', demoQueue, { passive: true });
      window.addEventListener('resize', demoQueue);
      setInterval(demoCheck, 1000); /* also catches the page shifting while images load */
      demoCheck();
      document.addEventListener('visibilitychange', demoSync);
      /* picking cross/custom hides the plaque panel, picking plaque shows it again */
      document.addEventListener('change', function () {
        setTimeout(demoSync, 60);
      });
      new MutationObserver(function () {
        demoSync();
      }).observe(root, { attributes: true, attributeFilter: ['class'] });
    }

    if (urn) {
      /* once per time in the box: show slide 1, the photo that carries the render; after that the customer can browse freely */
      var jumped = false;
      var warm = false;
      var jumpOnce = function () {
        if (jumped || !plaqueChosen()) return;
        jumped = true;
        jumpToHero();
      };
      input.addEventListener('focus', function () {
        if (!warm) {
          warm = true;
          new Image().src = urn.getAttribute('data-urn-img'); /* preload the render photo */
        }
        jumpOnce();
      });
      input.addEventListener('input', jumpOnce);
      input.addEventListener('blur', function () {
        jumped = false;
      });
      /* picking cross/custom hides the plaque panel: put the gallery photo back, or bring the render back */
      document.addEventListener('change', function () {
        setTimeout(render, 0);
      });
    }

    if (typeof ResizeObserver === 'function') {
      var ro = new ResizeObserver(fit);
      ro.observe(inner);
      if (urn) ro.observe(urnScene);
    } else {
      window.addEventListener('resize', fit);
    }
    if (document.fonts && document.fonts.load) {
      var ready = function () {
        fit();
      };
      document.fonts.load('600 40px "Playfair Display"').then(ready, ready);
      if (document.fonts.ready) document.fonts.ready.then(ready, ready);
    }
    textEl.style.fontFamily = FONT;
    input.style.fontFamily = FONT;
    if (urn) {
      urnText.style.fontFamily = FONT;
      urn.__refit = fit;
      initZoom(urn);
    }
    apply();
  }

  /* "Special instructions" checkbox: shows a second box that is only submitted while it is visible. */
  function initInstructions(root) {
    var scope = root.parentNode;
    var toggle = scope.querySelector('[data-plaque-instr-toggle]');
    var notes = scope.querySelector('[data-plaque-instr]');
    if (!toggle || !notes || toggle.getAttribute('data-instr-ready') === '1') return;
    toggle.setAttribute('data-instr-ready', '1');

    function sync() {
      /* never hide instructions that were typed: keep the box open (and sent) until the text is cleared */
      if (!toggle.checked && notes.value.replace(/\s/g, '') !== '') toggle.checked = true;
      var panelHidden = !!(scope.hidden || (scope.closest && scope.closest('[hidden]')));
      var on = toggle.checked && !panelHidden;
      notes.hidden = !toggle.checked;
      notes.disabled = !on; /* disabled fields are not submitted with the buy form */
      toggle.disabled = panelHidden;
    }

    toggle.addEventListener('change', function () {
      sync();
      if (toggle.checked) notes.focus();
    });
    /* the front-design chooser re-enables every field in a panel it shows: re-sync after it */
    document.addEventListener('change', function (e) {
      if (e.target !== toggle) setTimeout(sync, 0);
    });
    sync();
    setTimeout(sync, 0);
  }

  function initAll() {
    document.querySelectorAll('[data-plaque-live]').forEach(function (root) {
      init(root);
      initInstructions(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:select', initAll);
  window.__plaqueNormalize = normalize;
})();
